import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { sortBy } from 'lodash';
import { BaseModalWindow } from '../../modalWindows/BaseModalWindow';
import styles from './gridTabIndexSettings.module.scss';
import GridWrapper from '../../controls/GridWrapper';
import { Button } from '../../buttons/button';
import { CheckBox } from '../../controls/checkbox';
import { t } from 'i18next';
import { DocumentTabSettings } from '../../../libs/ui-settings-manager/interfaces';

export interface TabFieldConfig {
    id: string;
    label: string;
    required?: boolean;
    disabledCondition?: boolean;
}

export interface IGridTabIndexSettingsProps {
    documentKey: string;
    documentName: string;
    fields: TabFieldConfig[];
    settings: DocumentTabSettings;
    defaultSettings: DocumentTabSettings;
    onSave: (settings: DocumentTabSettings) => void;
    onCancel: () => void;
    className?: string;
}

export const GridTabIndexSettings: React.FC<IGridTabIndexSettingsProps> = ({
    documentKey,
    documentName,
    fields,
    settings: initialSettings,
    defaultSettings,
    onSave,
    onCancel,
    className
}) => {
    const [settings, setSettings] = useState<DocumentTabSettings>(initialSettings);
    const [dragItem, setDragItem] = useState<string | null>(null);
    const [dragOverItem, setDragOverItem] = useState<string | null>(null);

    useEffect(() => {
        setSettings(initialSettings);
    }, [initialSettings]);

    const handleToggleField = useCallback((fieldId: string) => {
        const isCurrentlyEnabled = settings.enabledFields.includes(fieldId);
        const field = fields.find(f => f.id === fieldId);

        // Проверяем, можно ли отключить поле
        // if (isCurrentlyEnabled && field?.required) {
        //     // Не отключаем обязательные поля
        //     return;
        // }

        let newEnabledFields: string[];
        let newTabOrder = [...settings.tabOrder];

        if (isCurrentlyEnabled) {
            newEnabledFields = settings.enabledFields.filter(id => id !== fieldId);
            newTabOrder = newTabOrder.filter(id => id !== fieldId);
        } else {
            newEnabledFields = [...settings.enabledFields, fieldId];
            if (!newTabOrder.includes(fieldId)) {
                newTabOrder.push(fieldId);
            }
        }

        setSettings({
            ...settings,
            enabledFields: newEnabledFields,
            tabOrder: newTabOrder
        });
    }, [settings, fields]);

    const handleDragStart = (e: React.DragEvent, fieldId: string) => {
        setDragItem(fieldId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', fieldId);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        const target = e.currentTarget as HTMLElement;
        const fieldId = target.dataset.fieldId;
        if (fieldId) {
            setDragOverItem(fieldId);
        }
    };

    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (dragItem && dragItem !== targetId && settings.enabledFields.includes(dragItem)) {
            const newTabOrder = [...settings.tabOrder];
            const dragIndex = newTabOrder.indexOf(dragItem);
            const targetIndex = newTabOrder.indexOf(targetId);

            if (dragIndex >= 0 && targetIndex >= 0) {
                newTabOrder.splice(dragIndex, 1);
                newTabOrder.splice(targetIndex, 0, dragItem);

                setSettings({
                    ...settings,
                    tabOrder: newTabOrder
                });
            }
        }
        setDragItem(null);
        setDragOverItem(null);
    };

    const handleDragEnd = () => {
        setDragItem(null);
        setDragOverItem(null);
    };

    const handleResetToDefault = () => {
        setSettings(defaultSettings);
    };

    const handleSave = () => {
        onSave(settings);
    };

    const sortedFields = useMemo(() => {
        return sortBy(
            fields.filter(field => settings.enabledFields.includes(field.id)),
            field => settings.tabOrder.indexOf(field.id)
        );
    }, [fields, settings.enabledFields, settings.tabOrder]);

    return (
        <BaseModalWindow
            header={`${t("modals.tabIndexSettings.header")} - ${documentName}`}
            ok={{ onClick: handleSave, title: 'Сохранить' }}
            cancel={{ onClick: onCancel, title: 'Отмена' }}
            className={className}
        >
            <div className={styles.container}>
                <GridWrapper cols={2} className={styles.headerControls}>
                    <Button
                        onClick={handleResetToDefault}
                        variant="outprimary"
                        className={styles.resetButton}
                    >
                        {t("modals.tabIndexSettings.handleResetToDefault")}
                    </Button>

                    {/* <div className={styles.autoSkipControl}>
                        <CheckBox
                            id={`${documentKey}-autoSkip`}
                            label="Автоматически пропускать отключенные поля"
                            defaultChecked={settings.autoSkipDisabled} 
                            onChanged={(checked) => {
                                setSettings({
                                    ...settings,
                                    autoSkipDisabled: checked
                                });
                            }}
                        />
                    </div> */}
                </GridWrapper>
                <GridWrapper cols={1} className={styles.headerControls}>
                    {settings.documentSettings?.booleanSettings.length && (
                        settings.documentSettings.booleanSettings.map((setting, index) => (
                            <CheckBox
                                key={setting.label}
                                id={`${documentKey}-${setting.label}`}
                                label={t(setting.label)}
                                defaultChecked={setting.state}
                                onChanged={(checked) => {
                                    if (settings.documentSettings === undefined) return
                                    const updatedBooleanSettings = [...settings.documentSettings.booleanSettings];
                                    updatedBooleanSettings[index] = {
                                        ...updatedBooleanSettings[index],
                                        state: checked
                                    };

                                    setSettings({
                                        ...settings,
                                        documentSettings: {
                                            ...settings.documentSettings,
                                            booleanSettings: updatedBooleanSettings
                                        }
                                    });
                                }}
                            />
                        ))
                    )}
                </GridWrapper>
                <div className={styles.sections}>
                    <div className={styles.fieldsSection}>
                        <p>{t("modals.tabIndexSettings.fields")}</p>
                        <div className={styles.fieldsList}>
                            {fields.map(field => {
                                const isEnabled = settings.enabledFields.includes(field.id);
                                const isRequired = field.required;

                                return (
                                    <div
                                        key={field.id}
                                        className={`${styles.fieldItem} ${isRequired ? styles.requiredField : ''
                                            }`}
                                    >
                                        <CheckBox
                                            id={`${documentKey}-field-${field.id}`}
                                            label={field.label}
                                            defaultChecked={isEnabled}
                                            // disabled={isRequired}
                                            onChanged={() => handleToggleField(field.id)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.orderSection}>
                        <p>{t("modals.tabIndexSettings.fieldsOrders")}</p>
                        <div className={styles.orderList}>
                            {sortedFields.map((field, index) => (
                                <div
                                    key={field.id}
                                    data-field-id={field.id}
                                    className={`${styles.orderItem} ${dragOverItem === field.id ? styles.dragOver : ''
                                        } ${field.disabledCondition ? styles.disabledField : ''}`}
                                    draggable={!field.disabledCondition}
                                    onDragStart={(e) => handleDragStart(e, field.id)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, field.id)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <div className={styles.orderContent}>
                                        <span className={styles.orderIndex}>{index + 1}</span>
                                        <span className={styles.orderLabel}>{field.label}</span>
                                        {field.required && (
                                            <span className={styles.requiredBadge}>{t("modals.tabIndexSettings.required")}</span>
                                        )}
                                        {field.disabledCondition && (
                                            <span className={styles.disabledBadge}>{t("modals.tabIndexSettings.reedOnly")}</span>
                                        )}
                                        {/* {!field.required && !field.disabledCondition && (
                                            <button
                                                type="button"
                                                className={styles.removeButton}
                                                onClick={() => handleToggleField(field.id)}
                                                aria-label={t("modals.tabIndexSettings.delTabIndex")}
                                            >
                                                ×
                                            </button>
                                        )} */}
                                    </div>
                                </div>
                            ))}

                            {sortedFields.length === 0 && (
                                <div className={styles.emptyState}>
                                    {t("modals.tabIndexSettings.notFieldsForTab")}
                                </div>
                            )}
                        </div>

                        <div className={styles.orderInfo}>
                            <p className={styles.infoText}>
                                <strong>{t("modals.tabIndexSettings.totalFieldsForTab")}</strong> {sortedFields.length} {t("modals.tabIndexSettings.from")} {fields.length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </BaseModalWindow>
    );
};